// MapView owns Leaflet + Supercluster imperatively. The map instance is
// controlled via refs, not Preact state, because Leaflet mutates the DOM
// itself. Markers are recomputed via a signal `effect()` that re-runs any
// time stations/fuel/viewport/visibleStations/selectedStationId change.

import { effect } from "@preact/signals";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useEffect, useRef } from "preact/hooks";
import { labelRules, leafletLayer, paintRules } from "protomaps-leaflet";
import Supercluster from "supercluster";

import { percentile } from "../geo";
import { lang, t } from "../i18n";
import { DEFAULT_VIEW, findRegion } from "../regions";
import {
  fuel,
  mapMode,
  regionKey,
  selectedStationId,
  stations,
  userLocation,
  viewport,
  visibleStations,
} from "../state";
import {
  BASEMAP_BOUNDS,
  BASEMAP_FLAVOR,
  TILE_ATTR,
  TILE_MAX_DATA_ZOOM,
  TILE_MIN_ZOOM,
  locateTarget,
  tileLang,
  tilesUrl,
} from "../tiles";
import type { Fuel, Station } from "../types";

// Fuels rendered in popups — order is important, matches the spec.
const TRACKED_FUELS: readonly Fuel[] = ["95", "98", "diesel"];

interface StationFeatureProps {
  stationId: string;
  /** Price for the CURRENTLY selected fuel — drives marker color. */
  price: number;
  /** ALL tracked fuel prices — used to render the popup. */
  prices: Partial<Record<Fuel, number>>;
  brand: string;
  town: string;
  address: string;
  hours: string;
}

type StationFeature = GeoJSON.Feature<GeoJSON.Point, StationFeatureProps>;

function buildIndex(
  items: Station[],
  f: Fuel,
): Supercluster<StationFeatureProps> {
  const features: StationFeature[] = items
    .filter((s) => s.prices[f] != null)
    .map((s) => ({
      type: "Feature",
      properties: {
        stationId: s.id,
        price: s.prices[f] as number,
        prices: s.prices,
        brand: s.brand,
        town: s.town,
        address: s.address,
        hours: s.hours,
      },
      geometry: { type: "Point", coordinates: [s.lng, s.lat] },
    }));
  const index = new Supercluster<StationFeatureProps>({
    radius: 60,
    maxZoom: 16,
  });
  index.load(features);
  return index;
}

function colorForPrice(
  price: number,
  p20: number,
  p40: number,
  p60: number,
  p80: number,
): string {
  if (price <= p20) return "#1a9850"; // green — cheapest 20%
  if (price <= p40) return "#91cf60"; // light green
  if (price <= p60) return "#fee08b"; // yellow — middle
  if (price <= p80) return "#fc8d59"; // orange
  return "#d73027"; // red — most expensive 20%
}

function stationDivIcon(color: string, isSelected: boolean): L.DivIcon {
  // Touch-friendly sizes: 24 px default / 32 px selected. Bigger is safer
  // for mobile tap targets (iOS HIG baseline is 44 px; we're a map, so
  // 24 px is a reasonable compromise between touchability and not
  // drowning the underlying geography).
  const size = isSelected ? 32 : 24;
  const borderWidth = isSelected ? 3 : 2;
  return L.divIcon({
    className: "station-marker",
    html: `<span style="display:block;width:${size}px;height:${size}px;border-radius:50%;background:${color};border:${borderWidth}px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.45)"></span>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

function clusterDivIcon(count: number): L.DivIcon {
  const size = count < 10 ? 32 : count < 100 ? 40 : 52;
  return L.divIcon({
    className: "station-cluster",
    html: `<span style="display:flex;align-items:center;justify-content:center;width:${size}px;height:${size}px;border-radius:50%;background:var(--app-accent, rgba(13,110,253,0.85));color:#fff;font-weight:600;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.3)">${count}</span>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

/** Escape user-controlled strings before interpolating into popup HTML. */
function escapeHtml(s: unknown): string {
  return String(s == null ? "" : s).replace(/[&<>"']/g, (c) => {
    const map: Record<string, string> = {
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#39;",
    };
    return map[c] ?? c;
  });
}

const FUEL_LABEL_KEYS: Record<Fuel, "fuel_95" | "fuel_98" | "fuel_diesel"> = {
  "95": "fuel_95",
  "98": "fuel_98",
  diesel: "fuel_diesel",
};

/** Render the popup HTML with all fuel prices + prominent Maps button. */
function renderPopupHtml(props: StationFeatureProps, lat: number, lng: number): string {
  const pricesHtml = TRACKED_FUELS.filter((f) => props.prices[f] != null)
    .map((f) => {
      const price = props.prices[f] as number;
      return `<div class="popup-price-row"><span class="popup-price-label">${escapeHtml(t(FUEL_LABEL_KEYS[f]))}</span><span class="popup-price-value">${price.toFixed(3)} €</span></div>`;
    })
    .join("");

  const mapsUrl = `https://www.google.com/maps?q=${lat},${lng}`;
  const hoursHtml = props.hours
    ? `<div class="popup-hours">${escapeHtml(props.hours)}</div>`
    : "";

  return (
    `<div class="popup-card">` +
    `<div class="popup-brand">${escapeHtml(props.brand)}</div>` +
    `<div class="popup-town">${escapeHtml(props.town)}</div>` +
    `<div class="popup-address">${escapeHtml(props.address)}</div>` +
    hoursHtml +
    `<div class="popup-prices">${pricesHtml}</div>` +
    `<a class="popup-maps" href="${mapsUrl}" target="_blank" rel="noopener noreferrer">` +
    `<span class="popup-maps-pin" aria-hidden="true">📍</span>` +
    `<span class="popup-maps-label">${escapeHtml(t("card_navigate"))}</span>` +
    `<span class="popup-maps-arrow" aria-hidden="true">→</span>` +
    `</a>` +
    `</div>`
  );
}

/**
 * Mobile gesture handling — "two-finger drag to pan" pattern used by
 * Google Maps / OSM embeds. On touch devices, disable Leaflet dragging
 * by default so single-finger swipes scroll the parent iframe page
 * instead of hijacking the gesture. Two-finger touch enables dragging
 * for the duration of that multi-touch sequence.
 *
 * Also shows a transient hint overlay when the user attempts a single-
 * finger drag, so the "why isn't this working?" question gets answered
 * immediately with a visual cue.
 */
/**
 * True only for primary-touch devices (phones, tablets — no mouse).
 * `L.Browser.touch` returns true on laptops with touchscreens (Chrome
 * sets `navigator.maxTouchPoints > 0`), which would wrongly disable mouse
 * dragging on desktop. The media query is precise: `hover: none` means
 * no pointer can hover, `pointer: coarse` means primary input is finger-
 * sized — both true only on actual mobile devices.
 */
function isPrimaryTouchDevice(): boolean {
  return window.matchMedia("(hover: none) and (pointer: coarse)").matches;
}

function setupTouchGestures(map: L.Map, hintEl: HTMLDivElement): () => void {
  if (!isPrimaryTouchDevice()) return () => {};

  map.dragging.disable();

  const container = map.getContainer();
  let hintTimer: number | undefined;

  const showHint = () => {
    hintEl.classList.add("visible");
    if (hintTimer) window.clearTimeout(hintTimer);
    hintTimer = window.setTimeout(() => {
      hintEl.classList.remove("visible");
    }, 1500);
  };

  const onTouchStart = (e: TouchEvent) => {
    if (e.touches.length >= 2) {
      map.dragging.enable();
      hintEl.classList.remove("visible");
      if (hintTimer) window.clearTimeout(hintTimer);
    }
  };

  const onTouchMove = (e: TouchEvent) => {
    if (e.touches.length === 1 && !map.dragging.enabled()) {
      showHint();
    }
  };

  const onTouchEnd = () => {
    // Small delay so in-progress drag can finish naturally.
    window.setTimeout(() => map.dragging.disable(), 80);
  };

  container.addEventListener("touchstart", onTouchStart, { passive: true });
  container.addEventListener("touchmove", onTouchMove, { passive: true });
  container.addEventListener("touchend", onTouchEnd, { passive: true });
  container.addEventListener("touchcancel", onTouchEnd, { passive: true });

  return () => {
    container.removeEventListener("touchstart", onTouchStart);
    container.removeEventListener("touchmove", onTouchMove);
    container.removeEventListener("touchend", onTouchEnd);
    container.removeEventListener("touchcancel", onTouchEnd);
    if (hintTimer) window.clearTimeout(hintTimer);
  };
}

export function MapView() {
  const containerRef = useRef<HTMLDivElement>(null);
  const hintRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const layerRef = useRef<L.LayerGroup | null>(null);
  // protomaps-leaflet's `leafletLayer()` is built via `L.GridLayer.extend()`
  // at runtime, but its .d.ts return type is a bare object literal that
  // doesn't structurally include L.Layer's members — cast to L.Layer so
  // map.addLayer/removeLayer accept it.
  const basemapRef = useRef<L.Layer | null>(null);
  // Label language the current basemap layer was built with.
  const basemapLangRef = useRef<string | null>(null);

  // One-time Leaflet init.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const inIframe = window.parent !== window;
    const map = L.map(containerRef.current, {
      center: [DEFAULT_VIEW.lat, DEFAULT_VIEW.lng],
      zoom: DEFAULT_VIEW.zoom,
      scrollWheelZoom: !inIframe,
      attributionControl: true,
      // The archive covers Spain only. Without these the user can pan or
      // zoom out into areas with no tiles, which render as blank white.
      maxBounds: BASEMAP_BOUNDS,
      minZoom: TILE_MIN_ZOOM,
    });

    const flavor = BASEMAP_FLAVOR;
    const activeLang = tileLang(__SITE__, lang.value);

    // Cast: leafletLayer() is built via L.GridLayer.extend() at runtime,
    // but its .d.ts return type doesn't structurally include L.Layer's
    // members, so TS can't verify addTo()/removeLayer() calls otherwise.
    const basemap = leafletLayer({
      url: tilesUrl(),
      maxDataZoom: TILE_MAX_DATA_ZOOM,
      paintRules: paintRules(flavor),
      labelRules: labelRules(flavor, activeLang),
      // A tile outside the archive's coverage (the 204 past its edge)
      // renders as sea instead of the container's grey. Almost every such
      // hole on a wide screen is ocean anyway.
      backgroundColor: flavor.water,
      attribution: TILE_ATTR,
    }) as unknown as L.Layer;
    basemap.addTo(map);
    basemapRef.current = basemap;
    basemapLangRef.current = activeLang;

    // The smallest zoom at which the whole viewport fits inside the
    // archive's outer tier. On a 1440-2560 px desktop, TILE_MIN_ZOOM alone
    // lets the view reach past the archive and show holes. Recomputed on
    // resize (window resize, and invalidateSize after a split/full toggle);
    // Leaflet clamps the current zoom up when it falls below the minimum.
    const fitMinZoom = () => {
      // getBoundsZoom clamps to the current minimum; drop it to the floor
      // while computing or a shrinking viewport could never lower it. Then
      // restore it, so setMinZoom fires zoomlevelschange (which re-enables
      // the zoom-out button) exactly when the minimum really changes.
      const prev = map.getMinZoom();
      map.options.minZoom = TILE_MIN_ZOOM;
      const next = Math.max(
        TILE_MIN_ZOOM,
        map.getBoundsZoom(L.latLngBounds(BASEMAP_BOUNDS), true),
      );
      map.options.minZoom = prev;
      map.setMinZoom(next);
    };
    fitMinZoom();
    map.on("resize", fitMinZoom);

    layerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;

    // Two-finger-drag gesture handler for touch devices.
    const teardownGestures = hintRef.current
      ? setupTouchGestures(map, hintRef.current)
      : () => {};

    const onMoveEnd = () => {
      const b = map.getBounds();
      viewport.value = {
        west: b.getWest(),
        south: b.getSouth(),
        east: b.getEast(),
        north: b.getNorth(),
      };
    };
    map.on("moveend", onMoveEnd);
    onMoveEnd();

    return () => {
      map.off("moveend", onMoveEnd);
      map.off("resize", fitMinZoom);
      teardownGestures();
      map.remove();
      mapRef.current = null;
      layerRef.current = null;
    };
  }, []);

  // Label language is baked into the layer at construction time, so the
  // embed's language switcher has to rebuild it. The effect also runs on
  // first mount (in every build), where the layer was just built with the
  // same language — skip unless the language actually changed. Fullweb
  // builds lock the language, so for them it never gets past that check.
  useEffect(() => {
    const map = mapRef.current;
    const previous = basemapRef.current;
    if (!map || !previous) return;

    const flavor = BASEMAP_FLAVOR;
    const activeLang = tileLang(__SITE__, lang.value);
    if (activeLang === basemapLangRef.current) return;
    const next = leafletLayer({
      url: tilesUrl(),
      maxDataZoom: TILE_MAX_DATA_ZOOM,
      paintRules: paintRules(flavor),
      labelRules: labelRules(flavor, activeLang),
      backgroundColor: flavor.water,
      attribution: TILE_ATTR,
    }) as unknown as L.Layer;
    next.addTo(map);
    map.removeLayer(previous);
    basemapRef.current = next;
    basemapLangRef.current = activeLang;
  }, [lang.value]);

  // Reactive marker rendering. The signal `effect` re-runs whenever any
  // of the signals it reads changes (stations, fuel, viewport,
  // visibleStations, selectedStationId, lang). useEffect's cleanup
  // disposes the signal effect on unmount — nested subscription
  // lifetimes stay correct.
  useEffect(() => {
    return effect(() => {
      const map = mapRef.current;
      const layer = layerRef.current;
      if (!map || !layer) return;

      const items = stations.value;
      const f = fuel.value;
      const v = viewport.value;
      const visibleIds = new Set(visibleStations.value.map((s) => s.id));
      const selectedId = selectedStationId.value;
      // Subscribe to lang so popup labels re-render on language change.
      void lang.value;
      if (items.length === 0 || !v) return;

      const index = buildIndex(items, f);
      const bbox: [number, number, number, number] = [
        v.west,
        v.south,
        v.east,
        v.north,
      ];
      const z = Math.round(map.getZoom());
      const clusters = index.getClusters(bbox, z);

      // Percentiles recomputed from currently visible stations only, so
      // pin colors re-rank as the user pans/zooms ("cheap here" vs
      // "cheap for all of Spain").
      const prices = visibleStations.value
        .map((s) => s.prices[f])
        .filter((p): p is number => p != null);
      const p20 = percentile(prices, 0.2);
      const p40 = percentile(prices, 0.4);
      const p60 = percentile(prices, 0.6);
      const p80 = percentile(prices, 0.8);

      layer.clearLayers();

      for (const c of clusters) {
        const [lng, lat] = c.geometry.coordinates;
        const isCluster = (c.properties as { cluster?: boolean }).cluster === true;

        if (isCluster) {
          const count = (c.properties as { point_count: number }).point_count;
          const m = L.marker([lat, lng], { icon: clusterDivIcon(count) });
          m.on("click", () => {
            const expansionZoom = index.getClusterExpansionZoom(
              (c.properties as { cluster_id: number }).cluster_id,
            );
            map.setView([lat, lng], expansionZoom);
          });
          m.addTo(layer);
        } else {
          const props = c.properties as StationFeatureProps;
          // Single-point clusters only rendered if their station survives
          // the region filter (visibleStations). This keeps the FilterBar's
          // region select actually affecting what shows on the map.
          if (!visibleIds.has(props.stationId)) continue;
          const isSelected = props.stationId === selectedId;
          const color = colorForPrice(props.price, p20, p40, p60, p80);
          const m = L.marker([lat, lng], {
            icon: stationDivIcon(color, isSelected),
          });
          m.bindPopup(renderPopupHtml(props, lat, lng), {
            maxWidth: 280,
            minWidth: 220,
            className: "fpw-popup",
          });
          m.on("click", () => {
            selectedStationId.value = props.stationId;
          });
          m.addTo(layer);
        }
      }
    });
  }, []);

  // React to region changes — fit the region's bbox (when available) or
  // fall back to its fixed view. fitBounds auto-calculates the zoom
  // needed for the region to fit the current viewport size — essential
  // on mobile where a fixed setView(lat, lng, 8) leaves a ton of empty
  // ocean around the Canarias archipelago because the aspect ratio
  // doesn't match the device. fitBounds respects both width + height.
  useEffect(() => {
    return effect(() => {
      const map = mapRef.current;
      const region = findRegion(regionKey.value);
      if (!map || !region) return;
      if (region.bbox) {
        map.fitBounds(
          [
            [region.bbox.south, region.bbox.west],
            [region.bbox.north, region.bbox.east],
          ],
          { animate: true, padding: [20, 20] },
        );
      } else if (region.view) {
        map.setView([region.view.lat, region.view.lng], region.view.zoom, {
          animate: true,
        });
      }
    });
  }, []);

  // React to station selection — fly the map to the selected pin.
  // Works for both sources: click on the map (sets selectedStationId
  // from within the marker effect) and click on a StationCard.
  useEffect(() => {
    return effect(() => {
      const map = mapRef.current;
      const id = selectedStationId.value;
      if (!map || !id) return;
      const station = stations.value.find((s) => s.id === id);
      if (!station) return;
      map.flyTo([station.lat, station.lng], Math.max(map.getZoom(), 14), {
        duration: 0.5,
      });
    });
  }, []);

  // React to userLocation — center the map on the user's position, or
  // on the nearest edge of the covered area when the user is outside it
  // (see locateTarget).
  useEffect(() => {
    return effect(() => {
      const map = mapRef.current;
      const loc = userLocation.value;
      if (!map || !loc) return;
      const target = locateTarget(loc.lat, loc.lng);
      map.setView([target.lat, target.lng], target.zoom, { animate: true });
    });
  }, []);

  // React to mapMode (split ↔ full) — Leaflet caches the container
  // dimensions and must be told after any layout change that affects
  // its parent's size. rAF lets the grid layout settle before the
  // invalidateSize call.
  useEffect(() => {
    return effect(() => {
      void mapMode.value;
      const map = mapRef.current;
      if (!map) return;
      requestAnimationFrame(() => map.invalidateSize());
    });
  }, []);

  return (
    <div class="map-wrap">
      <div ref={containerRef} class="map-view" />
      <div ref={hintRef} class="map-gesture-hint" aria-hidden="true">
        ✌️ {t("gesture_hint")}
      </div>
    </div>
  );
}
